import { CATEGORY_BY_ID } from '../categories';
import type { Registration } from '../registry';
import {
  INPUT_PIN_OPTIONS,
  OUTPUT_PIN_OPTIONS,
  PULL_PIN_OPTIONS,
  RTC_GPIO_OPTIONS,
} from '../pins';

const c = CATEGORY_BY_ID.gpio.colour;
const ci = CATEGORY_BY_ID.interrupts.colour;

export const gpioRegistration: Registration = {
  definitions: [
    {
      type: 'esp32_pin_mode',
      message0: 'set pin %1 to %2',
      args0: [
        { type: 'field_dropdown', name: 'PIN', options: PULL_PIN_OPTIONS },
        {
          type: 'field_dropdown',
          name: 'MODE',
          options: [
            ['OUTPUT', 'OUTPUT'],
            ['INPUT', 'INPUT'],
            ['INPUT_PULLUP', 'INPUT_PULLUP'],
            ['INPUT_PULLDOWN', 'INPUT_PULLDOWN'],
          ],
        },
      ],
      previousStatement: null,
      nextStatement: null,
      colour: c,
      tooltip: 'Configure a GPIO direction and pull resistor.',
    },
    {
      type: 'esp32_digital_write',
      message0: 'set pin %1 %2',
      args0: [
        { type: 'field_dropdown', name: 'PIN', options: OUTPUT_PIN_OPTIONS },
        {
          type: 'field_dropdown',
          name: 'LEVEL',
          options: [
            ['HIGH', 'HIGH'],
            ['LOW', 'LOW'],
          ],
        },
      ],
      previousStatement: null,
      nextStatement: null,
      colour: c,
      tooltip: 'Drive an output pin high or low.',
    },
    {
      type: 'esp32_pin_toggle',
      message0: 'toggle pin %1',
      args0: [{ type: 'field_dropdown', name: 'PIN', options: OUTPUT_PIN_OPTIONS }],
      previousStatement: null,
      nextStatement: null,
      colour: c,
      tooltip: 'Invert the current level of an output pin.',
    },
    {
      type: 'esp32_digital_read',
      message0: 'read pin %1',
      args0: [{ type: 'field_dropdown', name: 'PIN', options: INPUT_PIN_OPTIONS }],
      output: 'Boolean',
      colour: c,
      tooltip: 'Read a digital input as true (HIGH) / false (LOW).',
    },
    {
      type: 'esp32_on_interrupt',
      message0: 'on interrupt %1 %2',
      args0: [
        { type: 'field_dropdown', name: 'PIN', options: RTC_GPIO_OPTIONS },
        {
          type: 'field_dropdown',
          name: 'EDGE',
          options: [
            ['RISING', 'RISING'],
            ['FALLING', 'FALLING'],
            ['CHANGE', 'CHANGE'],
          ],
        },
      ],
      message1: 'do %1',
      args1: [{ type: 'input_statement', name: 'DO' }],
      colour: ci,
      tooltip:
        'Runs whenever the pin edge fires. Keep it short: no delay(), no Serial prints. Use a volatile flag to signal the main loop.',
    },
    {
      type: 'esp32_interrupt_detach',
      message0: 'detach interrupt on pin %1',
      args0: [{ type: 'field_dropdown', name: 'PIN', options: RTC_GPIO_OPTIONS }],
      previousStatement: null,
      nextStatement: null,
      colour: ci,
      tooltip: 'Remove an interrupt handler from a pin.',
    },
  ],
  metas: [
    {
      type: 'esp32_pin_mode',
      category: 'gpio',
      label: 'Set pin mode',
      description: 'Configure a GPIO as OUTPUT, INPUT, INPUT_PULLUP or INPUT_PULLDOWN.',
      fields: { PIN: { kind: 'dropdown', options: PULL_PIN_OPTIONS.map((o) => o[1]) }, MODE: { kind: 'dropdown', options: ['OUTPUT', 'INPUT', 'INPUT_PULLUP', 'INPUT_PULLDOWN'] } },
    },
    {
      type: 'esp32_digital_write',
      category: 'gpio',
      label: 'Digital write',
      description: 'Drive an output pin HIGH or LOW.',
      fields: { PIN: { kind: 'dropdown', options: OUTPUT_PIN_OPTIONS.map((o) => o[1]) }, LEVEL: { kind: 'dropdown', options: ['HIGH', 'LOW'] } },
    },
    {
      type: 'esp32_pin_toggle',
      category: 'gpio',
      label: 'Toggle pin',
      description: 'Invert the current level of an output pin.',
      fields: { PIN: { kind: 'dropdown', options: OUTPUT_PIN_OPTIONS.map((o) => o[1]) } },
    },
    {
      type: 'esp32_digital_read',
      category: 'gpio',
      label: 'Digital read',
      description: 'Value: true if the input pin reads HIGH, false otherwise.',
      fields: { PIN: { kind: 'dropdown', options: INPUT_PIN_OPTIONS.map((o) => o[1]) } },
    },
    {
      type: 'esp32_on_interrupt',
      category: 'interrupts',
      label: 'On interrupt',
      description: 'Event handler for a pin edge: RISING, FALLING or CHANGE. Body becomes an IRAM_ATTR ISR.',
      fields: {
        PIN: { kind: 'dropdown', options: RTC_GPIO_OPTIONS.map((o) => o[1]) },
        EDGE: { kind: 'dropdown', options: ['RISING', 'FALLING', 'CHANGE'] },
      },
      statementInput: 'DO',
      hat: true,
    },
    {
      type: 'esp32_interrupt_detach',
      category: 'interrupts',
      label: 'Detach interrupt',
      description: 'Detach the interrupt handler from a pin.',
      fields: { PIN: { kind: 'dropdown', options: RTC_GPIO_OPTIONS.map((o) => o[1]) } },
    },
  ],
};
